import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { Fragment, useEffect, useState } from 'react';

import ChevronDownIcon from '@/assets/icons/chevron-down.svg';
import { useConditionalTranslation } from '@/hooks';

export interface IOptionItem {
  label: React.ReactNode | string;

  value: number | string;

  disabled?: boolean;
}

interface IProps {
  id?: string | undefined;

  /** if the select is disabled, user can't change the value */
  disabled?: boolean;

  /** CSS classname, any given class will override existing class. */
  classnames?: string;

  /** CSS classname of menu item, any given class will override existing class. */
  menuItemClassnames?: string;

  /** inline CSS object */
  style?: object;

  /** variant of the input */
  variant?: 'solid' | 'plain';

  /* array of options */
  options: IOptionItem[];

  /* default option */
  defaultValue?: number | string;

  /* placeholder, only renders if default value is falsey */
  placeholder?: string;

  /* show/hide chevron down icon */
  chevron?: boolean;

  /** when user select an option from dropdown, onChange callback will be invoked */
  onChange?: (value: string) => void;
}

const getOptionCx = (active: boolean) => {
  return classNames('px-4 py-2 text-sm text-left appearance-none', {
    'bg-gray text-gray-900': active,
    'text-gray-700': !active,
  });
};

export const Select: React.FC<IProps> = ({
  id,
  onChange,
  disabled,
  options = [],
  defaultValue,
  chevron = true,
  placeholder = '',
  variant = 'solid',
  classnames,
  menuItemClassnames,
}) => {
  const translate = useConditionalTranslation();

  // show placeholder text if any of option haven't selected yet
  const [selectedLabel, setSelectedLabel] = useState<string | React.ReactNode | null>(
    null,
  );

  // if default value is exists change selected option label
  useEffect(() => {
    const defaultOption = options.filter(({ value }) => value === defaultValue);
    if (defaultOption.length > 0) {
      setSelectedLabel(defaultOption[0].label);
    }
  }, []);

  // if on change exists, invoke the callback. Also set label of selected option
  const handleOnSelect = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const { value: selectedItem } = event.currentTarget as HTMLInputElement;
    const selectedOption = options.filter(({ value }) => value == selectedItem);
    setSelectedLabel(selectedOption[0].label);
    if (typeof onChange === 'function') return onChange(selectedItem);
  };

  const containerCx = classNames(
    'inline-flex justify-between w-full px-4 py-2',
    {
      'bg-gray border border-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500':
        variant === 'solid',
      'cursor-not-allowed	': disabled,
    },
    classnames,
  );

  const menuItemCx = classNames(
    'absolute z-10 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
    menuItemClassnames,
  );

  return (
    <Menu as="div" className="relative inline-block text-left" id={id}>
      <div>
        <MenuButton className={containerCx} disabled={disabled} type="button">
          {selectedLabel ? translate(selectedLabel) : translate(placeholder)}
          {chevron && (
            <img
              src={ChevronDownIcon}
              alt="chevron icon"
              className="w-5 h-5 mt-[2px] ml-2 -mr-1"
              aria-hidden="true"
            />
          )}
        </MenuButton>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className={menuItemCx}>
          <div className="flex flex-col py-1 overflow-y-auto max-h-48">
            {options.map(({ value, label, disabled }) => {
              return (
                <MenuItem key={value} disabled={disabled}>
                  {({ focus }) => (
                    <button
                      onClick={handleOnSelect}
                      value={value}
                      className={getOptionCx(focus)}
                      type="button"
                    >
                      {translate(label)}
                    </button>
                  )}
                </MenuItem>
              );
            })}
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
};
